import React, { useState, useRef } from "react";
import { Modal, Button, Alert, Form, Table, Badge } from "react-bootstrap";
import axios from "../../services/axios";
import "./CsvUploadModal.scss";

const CsvUploadModal = ({ show, onHide, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      setFile(null);
      setPreview(null);
      setParseError(null);
      return;
    }

    // Validate file type
    if (!selectedFile.name.endsWith(".csv")) {
      setParseError("Please select a CSV file");
      setFile(null);
      setPreview(null);
      return;
    }

    setFile(selectedFile);
    setParseError(null);
    setError(null);

    // Parse and preview first few rows
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split("\n").filter((line) => line.trim());

        if (lines.length < 2) {
          setParseError(
            "CSV file must have at least a header row and one data row",
          );
          setPreview(null);
          return;
        }

        const headers = lines[0].split(",").map((h) => h.trim());
        const dataRows = lines.slice(1, 6).map((line) => {
          return line.split(",").map((cell) => cell.trim());
        });

        setPreview({
          headers,
          rows: dataRows,
          totalRows: lines.length - 1,
        });
      } catch (err) {
        setParseError("Failed to parse CSV file. Please check the format.");
        setPreview(null);
      }
    };

    reader.onerror = () => {
      setParseError("Failed to read file");
      setPreview(null);
    };

    reader.readAsText(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("csv_file", file);

      const response = await axios.post(
        "/domoreport/uploader/projectFeatureMap/csv",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const result = response.data;

      // Call success callback with summary
      if (onSuccess) {
        onSuccess({
          totalRows: result.totalRows || 0,
          recordsCreated: result.recordsCreated || 0,
          recordsUpdated: result.recordsUpdated || 0,
          errors: result.errors || [],
        });
      }

      // Reset state
      handleReset();
    } catch (err) {
      console.error("Error uploading CSV:", err);
      const apiError =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to upload CSV";
      setError(apiError);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setParseError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    if (!uploading) {
      handleReset();
      onHide();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={!uploading}
      className="csv-upload-modal"
    >
      <Modal.Header closeButton={!uploading}>
        <Modal.Title>
          <i className="fa fa-upload mr-2" />
          Upload Project Feature Maps CSV
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <i className="fa fa-exclamation-circle mr-2" />
            {error}
          </Alert>
        )}

        {parseError && (
          <Alert variant="warning">
            <i className="fa fa-exclamation-triangle mr-2" />
            {parseError}
          </Alert>
        )}

        <div className="upload-section mb-4">
          <h6 className="section-title">Select CSV File</h6>
          <Form.Group>
            <Form.Control
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <Form.Text className="text-muted">
              The CSV must include headers. Accepted columns: job_name (or
              name), job_id (or uuid), job_description (or description),
              trade_id, feature_ref_id, feature_action_ref_id,
              job_unit_of_meassure (or unitOfMeassure), project_priority_ref_id
            </Form.Text>
          </Form.Group>

          {file && (
            <div className="file-info mt-3">
              <Badge variant="info">
                <i className="fa fa-file-text-o mr-2" />
                {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </Badge>
            </div>
          )}
        </div>

        {preview && (
          <div className="preview-section">
            <h6 className="section-title">
              Preview (showing first 5 of {preview.totalRows} rows)
            </h6>
            <div className="table-responsive">
              <Table size="sm" bordered hover>
                <thead>
                  <tr>
                    {preview.headers.map((header, idx) => (
                      <th key={idx}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx}>
                          {cell || <span className="text-muted">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        <Alert variant="info" className="mt-3">
          <i className="fa fa-info-circle mr-2" />
          <strong>Upload Behavior:</strong>
          <ul className="mb-0 mt-2">
            <li>
              Existing records will be matched by ID, UUID, or Name (in that
              order)
            </li>
            <li>Matched records will be updated with new data</li>
            <li>New records will be created</li>
            <li>Any errors will be reported after upload</li>
          </ul>
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={!file || uploading || !!parseError}
        >
          {uploading ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" />
              Uploading...
            </>
          ) : (
            <>
              <i className="fa fa-upload mr-2" />
              Upload CSV
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CsvUploadModal;
